import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import Modal from "react-bootstrap/Modal";
import NavBar from "../../components/navbar/navbar.component";
import SideBar from "../../components/sidebar/sidebar.component";
import BroadcastEditor from "./BroadcastEditor";
import { deleteBroadcast, listBroadcasts, sendBroadcast, setBroadcastEnabled } from "./broadcastService";
import "./broadcast.css";

export default function Broadcast() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [broadcasts, setBroadcasts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastToDelete, setBroadcastToDelete] = useState(null);
  const editorRef = useRef(null);

  const loadBroadcasts = useCallback(async () => {
    setIsLoading(true);
    setListError("");
    try {
      setBroadcasts(await listBroadcasts());
    } catch {
      setListError("Unable to load broadcasts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBroadcasts();
  }, [loadBroadcasts]);

  const handlePreview = (event) => {
    event.preventDefault();
    const editor = editorRef.current;
    const nextErrors = {};
    if (!title.trim()) nextErrors.title = "A title is required.";
    const hasText = editor?.getText().replace(/\u200b/g, "").trim();
    const hasImage = editor?.getContents().ops.some((op) => op.insert?.image);
    if (!hasText && !hasImage) nextErrors.message = "A message is required.";
    setErrors(nextErrors);
    setSendError("");
    if (!Object.keys(nextErrors).length) setShowPreview(true);
  };

  const handleSend = async () => {
    if (isSending || isLoading || listError) return;
    setIsSending(true);
    setSendError("");
    try {
      await sendBroadcast({ title: title.trim(), message });
      setShowComposer(false);
      setShowPreview(false);
      setTitle("");
      editorRef.current.setText("");
      editorRef.current.history.clear();
      setMessage("");
      setErrors({});
      await loadBroadcasts();
    } catch {
      setSendError("Unable to send the broadcast. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const closeComposer = () => {
    if (isSending) return;
    editorRef.current?.setText("");
    editorRef.current?.history.clear();
    setTitle("");
    setMessage("");
    setErrors({});
    setSendError("");
    setShowPreview(false);
    setShowComposer(false);
  };

  const handleToggle = async (broadcast) => {
    if (pendingAction) return;
    setPendingAction(broadcast.id);
    setActionError("");
    try {
      await setBroadcastEnabled(broadcast.id, !broadcast.enabled);
      await loadBroadcasts();
    } catch {
      setActionError("Unable to update the broadcast. Please try again.");
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!broadcastToDelete || pendingAction) return;
    setPendingAction(broadcastToDelete.id);
    setActionError("");
    try {
      await deleteBroadcast(broadcastToDelete.id);
      setBroadcastToDelete(null);
      await loadBroadcasts();
    } catch {
      setActionError("Unable to delete the broadcast. Please try again.");
    } finally {
      setPendingAction(null);
    }
  };

  const messageExcerpt = (html) => {
    const container = document.createElement("template");
    container.innerHTML = DOMPurify.sanitize(html);
    container.content.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, br").forEach((element) => element.append(" "));
    const text = container.content.textContent.replace(/\s+/g, " ").trim();
    return text || (container.content.querySelector("img") ? "Image" : "");
  };

  return (
    <>
      <NavBar />
      <SideBar />
      <div className="content-page">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
                  <div className="header-title">
                    <h4 className="card-title mb-0">Broadcasts</h4>
                    <p className="mb-0 text-muted">Send a message to everyone.</p>
                  </div>
                  <button type="button" className="btn btn-primary" disabled={isLoading || Boolean(listError)} onClick={() => setShowComposer(true)}>Add Broadcast</button>
                </div>
                <div className="card-body">
                  {actionError && !broadcastToDelete && <div className="alert alert-danger" role="alert">{actionError}</div>}
                  {isLoading ? <p role="status">Loading broadcasts...</p> : listError ? (
                    <div className="alert alert-danger" role="alert">
                      {listError}
                      <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={loadBroadcasts}>Retry</button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered broadcast-table" aria-label="Broadcasts">
                        <thead>
                          <tr><th scope="col">Title</th><th scope="col">Message</th><th scope="col">Created</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
                        </thead>
                        <tbody>
                          {broadcasts.length === 0 ? <tr><td colSpan={5} className="text-center">No broadcasts yet.</td></tr> : broadcasts.map((broadcast) => (
                            <tr key={broadcast.id}>
                              <th scope="row">{broadcast.title}</th>
                              <td><div className="broadcast-table-excerpt">{messageExcerpt(broadcast.message)}</div></td>
                              <td>{broadcast.createdAt ? new Date(broadcast.createdAt).toLocaleString() : "—"}</td>
                              <td><span className={`badge ${broadcast.enabled ? "badge-success" : "badge-secondary"}`}>{broadcast.enabled ? "Enabled" : "Disabled"}</span></td>
                              <td>
                                <div className="broadcast-table-actions">
                                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setSelectedBroadcast(broadcast)}>View</button>
                                  <button type="button" className="btn btn-sm btn-secondary" disabled={Boolean(pendingAction)} onClick={() => handleToggle(broadcast)}>{broadcast.enabled ? "Disable" : "Enable"}</button>
                                  <button type="button" className="btn btn-sm btn-danger" disabled={Boolean(pendingAction)} onClick={() => { setActionError(""); setBroadcastToDelete(broadcast); }}>Delete</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showComposer} onHide={closeComposer} size="lg" centered scrollable aria-labelledby="broadcast-composer-title">
        <Modal.Header>
          <Modal.Title id="broadcast-composer-title" as="h5">{showPreview ? title.trim() : "Add Broadcast"}</Modal.Title>
          <button type="button" className="close" aria-label="Close" disabled={isSending} onClick={closeComposer}>
            <span aria-hidden="true">&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body>
          <form id="broadcast-form" onSubmit={handlePreview} noValidate hidden={showPreview}>
            <div className="form-group">
              <label htmlFor="broadcast-title">Title</label>
              <input
                id="broadcast-title"
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Enter a short broadcast title"
                maxLength={120}
                required
                aria-invalid={Boolean(errors.title)}
                aria-describedby={errors.title ? "broadcast-title-error" : undefined}
              />
              {errors.title && <div id="broadcast-title-error" className="invalid-feedback">{errors.title}</div>}
            </div>
            <div className="form-group">
              <label id="broadcast-message-label">Message</label>
              <BroadcastEditor editorRef={editorRef} onChange={setMessage} invalid={Boolean(errors.message)} />
              {errors.message && <div id="broadcast-message-error" className="invalid-feedback d-block">{errors.message}</div>}
            </div>
          </form>
          <div hidden={!showPreview} className="ql-snow ql-container broadcast-message-content">
            <div className="ql-editor" dangerouslySetInnerHTML={{ __html: message }} />
          </div>
          {sendError && <div className="alert alert-danger mt-3" role="alert">{sendError}</div>}
        </Modal.Body>
        <Modal.Footer>
          {showPreview ? <>
            <button type="button" className="btn btn-secondary" disabled={isSending} onClick={() => setShowPreview(false)}>Back to Edit</button>
            <button type="button" className="btn btn-primary" disabled={isSending || isLoading || Boolean(listError)} onClick={handleSend}>
              {isSending ? "Sending..." : "Send Broadcast"}
            </button>
          </> : <>
            <button type="button" className="btn btn-secondary" onClick={closeComposer}>Cancel</button>
            <button type="submit" form="broadcast-form" className="btn btn-primary">Preview Broadcast</button>
          </>}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(selectedBroadcast)} onHide={() => setSelectedBroadcast(null)} centered scrollable aria-labelledby="saved-broadcast-title">
        <Modal.Header>
          <Modal.Title id="saved-broadcast-title" as="h5">{selectedBroadcast?.title}</Modal.Title>
          <button type="button" className="close" aria-label="Close" onClick={() => setSelectedBroadcast(null)}><span aria-hidden="true">&times;</span></button>
        </Modal.Header>
        <Modal.Body>
          <div className="ql-snow ql-container broadcast-message-content">
            <div className="ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedBroadcast?.message || "") }} />
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(broadcastToDelete)} onHide={() => !pendingAction && setBroadcastToDelete(null)} centered aria-labelledby="delete-broadcast-title">
        <Modal.Header><Modal.Title id="delete-broadcast-title" as="h5">Delete Broadcast</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Delete “{broadcastToDelete?.title}”? This cannot be undone.</p>
          {actionError && <div className="alert alert-danger" role="alert">{actionError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" disabled={Boolean(pendingAction)} onClick={() => setBroadcastToDelete(null)}>Cancel</button>
          <button type="button" className="btn btn-danger" disabled={Boolean(pendingAction)} onClick={handleDelete}>{pendingAction ? "Deleting..." : "Delete Broadcast"}</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
